import { createRouter, createWebHashHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import type { App } from 'vue'
import { Layout } from '@/utils/routerHelper'
import { useI18n } from '@/hooks/web/useI18n'
import { NO_RESET_WHITE_LIST } from '@/constants'

const { t } = useI18n()

export const constantRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/',
    component: Layout,
    redirect: '/dashboard/analysis',
    name: 'Root',
    meta: {
      hidden: true
    }
  },
  {
    path: '/redirect',
    component: Layout,
    name: 'Redirect',
    children: [
      {
        path: '/redirect/:path(.*)',
        name: 'Redirect',
        component: () => import('@/views/Redirect/Redirect.vue'),
        meta: {}
      }
    ],
    meta: {
      hidden: true,
      noTagsView: true
    }
  },
  {
    path: '/login',
    component: () => import('@/views/Login/Login.vue'),
    name: 'Login',
    meta: {
      hidden: true,
      title: t('router.login'),
      noTagsView: true
    }
  },
  {
    path: '/personal',
    component: Layout,
    redirect: '/personal/personal-center',
    name: 'Personal',
    meta: {
      title: t('router.personal'),
      hidden: true,
      canTo: true
    },
    children: [
      {
        path: 'personal-center',
        component: () => import('@/views/Personal/PersonalCenter/PersonalCenter.vue'),
        name: 'PersonalCenter',
        meta: {
          title: t('router.personalCenter'),
          hidden: true,
          canTo: true
        }
      }
    ]
  },
  {
    path: '/404',
    component: () => import('@/views/Error/404.vue'),
    name: 'NoFind',
    meta: {
      hidden: true,
      title: '404',
      noTagsView: true
    }
  }
]

export const asyncRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/dashboard',
    component: Layout,
    name: 'Dashboard',
    meta: {
      title: t('router.overview'),
      roles: ['admin', 'editor', 'viewer'] // 所有角色都可以访问
    },
    children: [
      {
        path: 'analysis',
        component: () => import('@/views/Dashboard/Analysis.vue'),
        name: 'Analysis',
        meta: {
          icon: 'vi-ant-design:dashboard-filled',
          title: t('router.analysis'),
          roles: ['admin', 'editor']
        }
      }
      // {
      //   path: 'analysisIndustry',
      //   component: () => import('@/views/Dashboard/AnalysisIndustry.vue'),
      //   name: 'AnalysisIndustry',
      //   meta: {
      //     icon: 'vi-ant-design:pie-chart-outlined',
      //     title: t('router.analysisIndustry'),
      //     roles: ['admin', 'editor']
      //   }
      // }
    ]
  },
  {
    path: '/selfPerson',
    component: Layout,
    name: 'SelfPerson',
    meta: {
      title: t('router.selfPerson'),
      roles: ['personal'],
      icon: 'material-symbols:account-tree-outline-rounded'
    },
    children: [
      {
        path: 'selfPerson',
        component: () => import('@/views/ReviewData/ReviewSelfPerson.vue'),
        name: 'ReviewSelfPerson',
        meta: {
          title: t('router.selfPerson'),
          roles: ['personal'],
          noCache: false // 开启缓存
        }
      }
    ]
  },
  {
    path: '/selfCompany',
    component: Layout,
    name: 'SelfCompany',
    meta: {
      title: t('router.selfCompany'),
      roles: ['company'],
      icon: 'material-symbols:account-tree-outline-rounded'
    },
    children: [
      {
        path: 'selfCompany',
        component: () => import('@/views/ReviewData/ReviewSelfComp.vue'),
        name: 'ReviewSelfCompany',
        meta: {
          title: t('router.selfCompany'),
          roles: ['company'],
          noCache: false // 开启缓存
        }
      },
      {
        path: 'companyPerson',
        component: () => import('@/views/ReviewData/ReviewCompanyPerson.vue'),
        name: 'ReviewCompanyPerson',
        meta: {
          title: t('router.companyPerson'),
          roles: ['company'],
          noCache: false // 开启缓存
        }
      },
      {
        path: 'companyPatent',
        component: () => import('@/views/ReviewData/ReviewCompanyPatent.vue'),
        name: 'ReviewCompanyPatent',
        meta: {
          title: t('router.companyPatent'),
          roles: ['company'],
          noCache: false // 开启缓存
        }
      },
      {
        path: 'companyRecruit',
        component: () => import('@/views/ReviewData/ReviewCompanyRecruit.vue'),
        name: 'ReviewCompanyRecruit',
        meta: {
          title: t('router.companyRecruit'),
          roles: ['company'],
          noCache: false // 开启缓存
        }
      },
      {
        path: 'companyProduct',
        component: () => import('@/views/ReviewData/ReviewCompanyProduct.vue'),
        name: 'ReviewCompanyProduct',
        meta: {
          title: t('router.companyProduct'),
          roles: ['company'],
          noCache: false // 开启缓存
        }
      },
      {
        path: 'companyHelp',
        component: () => import('@/views/ReviewData/ReviewCompanyHelp.vue'),
        name: 'ReviewCompanyHelp',
        meta: {
          title: t('router.companyHelp'),
          roles: ['company'],
          noCache: false // 开启缓存
        }
      }
    ]
  },
  {
    path: '/fullScreen',
    name: 'FullScreen',
    component: () => import('@/views/ReviewData/StarView.vue'),
    meta: {
      title: t('router.starView'),
      roles: ['admin', 'editor'],
      icon: 'material-symbols:account-tree-outline-rounded',
      noCache: false, // 开启缓存
      noTagsView: true // 全屏模式不显示标签
    }
  },
  {
    path: '/starView',
    component: Layout,
    name: 'starView',
    meta: {
      title: t('router.starView'),
      roles: ['admin', 'editor'],
      hidden: true
    },
    children: [
      {
        path: 'starView',
        component: () => import('@/views/ReviewData/StarView.vue'),
        name: 'StarView',
        meta: {
          icon: 'material-symbols:account-tree-outline-rounded',
          title: t('router.starView'),
          noCache: false // 开启缓存
        }
      }
    ]
  },
  // {
  //   path: '/inputResearch',
  //   component: Layout,
  //   name: 'InputResearch',
  //   meta: {
  //     title: t('router.inputResearch'),
  //     icon: 'material-symbols:add-chart-outline',
  //     roles: ['admin', 'editor', 'company', 'personal'] // 只有admin和editor可以访问
  //   },
  //   children: [
  //     {
  //       path: 'inputKnowledge',
  //       component: () => import('@/views/Research/CompKnowledge.vue'),
  //       name: 'CompKnowledge',
  //       meta: {
  //         title: t('router.inputCompProj'),
  //         roles: ['admin', 'editor', 'company', 'personal']
  //       }
  //     },
  //     {
  //       path: 'inputCompProj',
  //       component: () => import('@/views/Research/CompProject.vue'),
  //       name: 'CompProject',
  //       meta: {
  //         title: t('router.inputKnowledge'),
  //         roles: ['admin', 'editor', 'company', 'personal']
  //       }
  //     }
  //   ]
  // },
  {
    path: '/shareSpace',
    component: Layout,
    name: 'ShareSpace',
    meta: {
      title: t('router.shareSpace'),
      icon: 'material-symbols:contacts-product'
    },
    children: [
      {
        path: 'sharePerson',
        component: () => import('@/views/ReviewData/SharePerson.vue'),
        name: 'SharePerson',
        meta: {
          title: t('router.sharePerson')
        }
      },
      {
        path: 'shareComp',
        component: () => import('@/views/ReviewData/ShareComp.vue'),
        name: 'ShareComp',
        meta: {
          title: t('router.shareCompany')
        }
      },
      {
        path: 'companyHelp',
        component: () => import('@/views/ReviewData/ReviewCompanyHelp.vue'),
        name: 'companyHelp',
        meta: {
          title: t('router.companyHelp'),
          roles: ['admin', 'editor'],
          noCache: false // 开启缓存
        }
      },
      {
        path: 'inputCompProj',
        component: () => import('@/views/Research/CompProject.vue'),
        name: 'inputCompProj',
        meta: {
          title: t('router.inputKnowledge'),
          roles: ['admin', 'editor', 'company', 'personal']
        }
      },
      {
        path: 'industrySupport',
        component: () => import('@/views/IndustrySupport/IndustrySupport.vue'),
        name: 'IndustrySupport',
        meta: {
          title: '创业支持',
          roles: ['admin', 'editor', 'company', 'personal']
        }
      }
      // {
      //   path: 'inputKnowledge',
      //   component: () => import('@/views/Research/CompKnowledge.vue'),
      //   name: 'CompKnowledge',
      //   meta: {
      //     title: t('router.inputCompProj'),
      //     roles: ['admin', 'editor', 'company', 'personal']
      //   }
      // },
      // {
      //   path: 'inputScientific',
      //   component: () => import('@/views/ReviewData/InputScientific.vue'),
      //   name: 'InputScientific',
      //   meta: {
      //     title: t('router.inputScientific'),
      //     roles: ['admin', 'editor', 'company']
      //   }
      // }
    ]
  },
  // {
  //   path: '/inputTraining',
  //   component: Layout,
  //   name: 'InputTraining',
  //   meta: {
  //     title: t('router.inputTraining'),
  //     roles: ['admin', 'editor'],
  //     icon: 'material-symbols:add-chart-outline'
  //   },
  //   children: [
  //     {
  //       path: 'personTraining',
  //       component: () => import('@/views/ReviewData/ReviewTraining.vue'),
  //       name: 'PersonTraining',
  //       meta: {
  //         title: t('router.trainingClass')
  //       }
  //     },
  //     {
  //       path: 'netPersonTraining',
  //       component: () => import('@/views/ReviewData/ReviewNetTraining.vue'),
  //       name: 'NetPersonTraining',
  //       meta: {
  //         title: t('router.trainingNetClass')
  //       }
  //     },
  //     {
  //       path: 'trainingPerson',
  //       component: () => import('@/views/ReviewData/ReviewTrainingPerson.vue'),
  //       name: 'TrainingPerson',
  //       meta: {
  //         title: t('router.trainingPerson')
  //       }
  //     }
  //   ]
  // },
  {
    path: '/reviewData',
    component: Layout,
    name: 'ReviewData',
    meta: {
      title: t('router.reviewData'),
      roles: ['admin', 'editor'],
      icon: 'material-symbols:feature-search-outline-rounded'
    },
    children: [
      {
        path: 'reviewIndus',
        component: () => import('@/views/ReviewData/ReviewIndu.vue'),
        name: 'ReviewIndus',
        meta: {
          title: t('router.inputIndus')
        }
      },
      {
        path: 'reviewJob',
        component: () => import('@/views/ReviewData/ReviewJob.vue'),
        name: 'ReviewJob',
        meta: {
          title: t('router.inputJob')
        }
      },
      {
        path: 'reviewTech',
        component: () => import('@/views/ReviewData/ReviewTech.vue'),
        name: 'ReviewTech',
        meta: {
          title: t('router.inputTech')
        }
      },
      {
        path: 'reviewComp',
        component: () => import('@/views/ReviewData/ReviewComp.vue'),
        name: 'ReviewComp',
        meta: {
          title: t('router.inputComp')
        }
      },
      {
        path: 'reviewPerson',
        component: () => import('@/views/ReviewData/ReviewPerson.vue'),
        name: 'ReviewPerson',
        meta: {
          title: t('router.inputPerson')
        }
      },
      {
        path: 'reviewMajor',
        component: () => import('@/views/ReviewData/ReviewMajor.vue'),
        name: 'ReviewMajor',
        meta: {
          title: t('router.inputMajor')
        }
      },
      {
        path: 'reviewProduct',
        component: () => import('@/views/ReviewData/ReviewProduct.vue'),
        name: 'ReviewProduct',
        meta: {
          title: t('router.inputProduct')
        }
      },
      //  {
      //   path: 'reviewUser',
      //   component: () => import('@/views/ReviewData/ReviewUser.vue'),
      //   name: 'reviewUser',
      //   meta: {
      //     title: t('router.inputRole')
      //   }
      // },
      {
        path: 'reviewExcelInfo',
        component: () => import('@/views/ReviewData/ReviewExcelInfo.vue'),
        name: 'ReviewExcelInfo',
        meta: {
          title: t('router.tongbuInfo')
        }
      }
    ]
  },
  {
    path: '/message',
    component: Layout,
    name: 'Message',
    meta: {
      title: t('router.message'),
      icon: 'material-symbols:add-chart-outline',
      hidden: true
    },
    children: [
      {
        path: 'message',
        component: () => import('@/views/ReviewData/ReviewMessage.vue'),
        name: 'ReviewMessage',
        meta: {
          title: t('router.message'),
          hidden: true
        }
      },
      {
        path: 'messageSummary',
        component: () => import('@/views/ReviewData/ReviewMessageSummary.vue'),
        name: 'ReviewMessageSummary',
        meta: {
          title: t('router.messageSummary'),
          hidden: true
        }
      },
      {
        path: 'recruitData',
        component: () => import('@/views/ReviewData/ReviewRecruitData.vue'),
        name: 'ReviewRecruitData',
        meta: {
          title: t('router.recruitData'),
          hidden: true
        }
      },
      {
        path: 'intentionData',
        component: () => import('@/views/ReviewData/ReviewIntentionData.vue'),
        name: 'ReviewIntentionData',
        meta: {
          title: t('router.intentionData'),
          hidden: true
        }
      }
    ]
  },
  {
    path: '/inputData',
    component: Layout,
    name: 'InputData',
    meta: {
      title: t('router.inputData'),
      icon: 'material-symbols:add-chart-outline',
      hidden: true
    },
    children: [
      // {
      //   path: 'inputIndus',
      //   component: () => import('@/views/InputData/InputIndus.vue'),
      //   name: 'InputIndus',
      //   meta: {
      //     title: t('router.inputIndus')
      //   }
      // },
      // {
      //   path: 'inputJob',
      //   component: () => import('@/views/InputData/InputJob.vue'),
      //   name: 'InputJob',
      //   meta: {
      //     title: t('router.inputJob')
      //   }
      // },
      // {
      //   path: 'inputTech',
      //   component: () => import('@/views/InputData/InputTech.vue'),
      //   name: 'InputTech',
      //   meta: {
      //     title: t('router.inputTech')
      //   }
      // },
      // {
      //   path: 'inputComp',
      //   component: () => import('@/views/InputData/InputComp.vue'),
      //   name: 'InputComp',
      //   meta: {
      //     title: t('router.inputComp')
      //   }
      // },
      // {
      //   path: 'inputPerson',
      //   component: () => import('@/views/InputData/InputPerson.vue'),
      //   name: 'InputPerson',
      //   meta: {
      //     title: t('router.inputPerson')
      //   }
      // },
      {
        path: 'inputInformation',
        component: () => import('@/views/InputData/InputInformation.vue'),
        name: 'InputInformation',
        meta: {
          title: t('router.inputZongHe'),
          hidden: true
        }
      }
    ]
  },
  {
    path: '/reviewUser',
    component: Layout,
    name: 'ReviewUser',
    meta: {
      title: t('router.inputRole'),
      roles: ['admin'] // 只有admin可以访问用户管理
    },
    children: [
      {
        path: 'userManager',
        component: () => import('@/views/ReviewData/ReviewUser.vue'),
        name: 'userManager',
        meta: {
          icon: 'material-symbols:account-box',
          title: t('router.inputRole'),
          roles: ['admin']
        }
      }
    ]
  }
  // {
  //   path: '/reviewUser',
  //   component: () => import('@/views/ReviewData/ReviewUser.vue'),
  //   name: 'ReviewUser',
  //   meta: {
  //     title: t('router.inputRole')
  //   }
  // },
  // {
  //   path: '/sysSetting',
  //   component: Layout,
  //   name: 'SysSetting',
  //   meta: {
  //     title: t('router.sysSetting'),
  //     icon: 'material-symbols:manufacturing-rounded'
  //   }
  // }
]

const router = createRouter({
  history: createWebHashHistory(),
  strict: true,
  routes: constantRouterMap as RouteRecordRaw[],
  scrollBehavior: () => ({ left: 0, top: 0 })
})

export const resetRouter = (): void => {
  router.getRoutes().forEach((route) => {
    const { name } = route
    if (name && !NO_RESET_WHITE_LIST.includes(name as string)) {
      router.hasRoute(name) && router.removeRoute(name)
    }
  })
}

export const setupRouter = (app: App<Element>) => {
  app.use(router)
}

export default router
